import type { ProjectRole } from '../types/workspace.types';

/** The projects shown in the sidebar tree: the ones the person opened most recently. */
export const RECENT_PROJECT_LIMIT = 10;

export interface RecentProject {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly role: ProjectRole;
}

const KEY_PREFIX = 'conbent.recentProjects';
const CHANGE_EVENT = 'conbent:recent-projects';

/** One list per signed-in person, so a shared browser does not show one person's projects to another. */
function storageKey(owner: string): string {
  return `${KEY_PREFIX}.${owner}`;
}

function read(owner: string): string {
  try {
    return localStorage.getItem(storageKey(owner)) ?? '[]';
  } catch {
    // Storage can be disabled; the tree then simply has nothing to remember.
    return '[]';
  }
}

function parse(raw: string): readonly RecentProject[] {
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];

    return (value as readonly unknown[])
      .filter(
        (entry): entry is RecentProject =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as RecentProject).id === 'string' &&
          typeof (entry as RecentProject).name === 'string' &&
          typeof (entry as RecentProject).code === 'string' &&
          typeof (entry as RecentProject).role === 'string'
      )
      .slice(0, RECENT_PROJECT_LIMIT);
  } catch {
    return [];
  }
}

// `useSyncExternalStore` needs the same array back while nothing changed.
const cache = new Map<string, { readonly raw: string; readonly list: readonly RecentProject[] }>();

export function readRecentProjects(owner: string): readonly RecentProject[] {
  const raw = read(owner);
  const cached = cache.get(owner);
  if (cached?.raw === raw) return cached.list;

  const list = parse(raw);
  cache.set(owner, { raw, list });
  return list;
}

/** Puts a project first, removes any earlier entry for it, and keeps only the newest ten. */
export function recordRecentProject(owner: string, project: RecentProject): void {
  const current = readRecentProjects(owner);
  const unchanged = current[0]?.id === project.id && current[0].name === project.name &&
    current[0].code === project.code && current[0].role === project.role;
  if (unchanged) return;

  const next = [project, ...current.filter((entry) => entry.id !== project.id)].slice(
    0,
    RECENT_PROJECT_LIMIT
  );

  try {
    localStorage.setItem(storageKey(owner), JSON.stringify(next));
  } catch {
    return;
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribeRecentProjects(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange);
  // Another tab opening a project updates this tab's tree too.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}
